import { CheckBoxOutlineBlank, Square } from "@mui/icons-material";
import {
    Box,
    Checkbox,
    FormControlLabel,
    ListItemText,
    MenuItem,
    styled,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { AutoSizer, List, ListRowProps } from "react-virtualized";
import { IListItem } from "../../interface/list-item";
import { IWithClassName } from "../../theme/default.theme";
import MultiSelect from "../multi-select/multi-select";

interface IOwnProps extends IWithClassName {
    selectedItems: Array<IListItem>;
    options: Array<IListItem>;
    onChange?: (list: Array<IListItem>) => void;
    placeHolder?: string;
    multiline?: boolean;
    disableSearch?: boolean;
    searchFunction?: (text: string) => Array<IListItem>;
    getChipText?: (item: IListItem) => string;
    clearAllOption?: boolean;
    inputMask?: RegExp | string;
    rowHeight?: number;
    menuWidth: number;
    disable?: boolean;
    getOptionTitle?: (option: IListItem) => string | undefined;
    onEnterKeyError?: (errorMessage: string) => void;
}

const MultiSelectList = ({
    selectedItems,
    options,
    onChange,
    placeHolder,
    multiline,
    className,
    disableSearch,
    searchFunction,
    getChipText,
    clearAllOption,
    inputMask,
    rowHeight = 30,
    disable,
    getOptionTitle,
    onEnterKeyError,
}: IOwnProps) => {
    const [maxMenuHeight, setMaxMenuHeight] = useState<number>(0);
    const [filteredOption, setFilteredOptions] =
        useState<Array<IListItem>>(options);

    useEffect(() => {
        setFilteredOptions(options);
    }, [options]);

    const selectedOptionsToDisplay = useMemo(
        () => [
            ...selectedItems,
            ...options.filter(
                (item) =>
                    item.alwaysSelected &&
                    !selectedItems.find((selected) => selected.id === item.id),
            ),
        ],
        [selectedItems, options],
    );

    const onChangeHandler = (item: IListItem, isChecked: boolean) => {
        const newSelectedItems = isChecked
            ? [...selectedItems, item]
            : selectedItems.filter((value) => value.id !== item.id);
        onChange?.(newSelectedItems);
    };

    const onSearch =
        searchFunction ??
        ((text: string) =>
            options.filter(
                (option) =>
                    option.name.toLowerCase().indexOf(text.toLowerCase()) >=
                        0 || option.id.toString() === text.trim(),
            ));

    return (
        <MultiSelect
            onChangeHandler={onChangeHandler}
            placeHolder={placeHolder}
            selectedValue={selectedOptionsToDisplay}
            multiline={multiline}
            disableSearch={!!disableSearch}
            onSearchTextChange={(text) => setFilteredOptions(onSearch(text))}
            getChipText={getChipText}
            clearAllOption={clearAllOption}
            onChange={onChange}
            setMenuHeight={setMaxMenuHeight}
            inputMask={inputMask}
            disable={disable}
            options={options}
            filteredOptions={filteredOption}
            onEnterKeyError={onEnterKeyError}
        >
            <MenuOptions
                selectedItems={selectedOptionsToDisplay}
                options={filteredOption}
                onChangeHandler={onChangeHandler}
                className={className}
                maxMenuHeight={maxMenuHeight}
                rowHeight={rowHeight}
                getOptionTitle={getOptionTitle}
            />
        </MultiSelect>
    );
};

interface IMenuOptions extends IWithClassName {
    selectedItems: Array<IListItem>;
    options: Array<IListItem>;
    onChangeHandler: (selected: IListItem, isChecked: boolean) => void;
    maxMenuHeight: number;
    rowHeight: number;
    getOptionTitle?: (option: IListItem) => string | undefined;
}

const MenuOptions = ({
    options,
    onChangeHandler,
    selectedItems,
    className,
    maxMenuHeight,
    rowHeight,
    getOptionTitle,
}: IMenuOptions) => {
    const selectedIdsMap = useMemo(
        () =>
            selectedItems.reduce(
                (map, selected) => {
                    map[selected.id] = true;
                    return map;
                },
                {} as Record<string, boolean>,
            ),
        [selectedItems],
    );

    const menuHeight = useMemo(
        () => Math.min(maxMenuHeight, rowHeight * options.length),
        [maxMenuHeight, options],
    );

    const handleItemSelect = (option: IListItem, checked: boolean) => {
        if (!option.readonly) {
            onChangeHandler(option, checked);
        }
    };

    const rowRenderer = ({ index, style }: ListRowProps) => {
        const opt = options[index];
        const isChecked = selectedIdsMap[opt.id] ?? false;

        return (
            <MenuItem
                value={opt.id}
                key={opt.id}
                style={style}
                title={getOptionTitle?.(opt)}
            >
                <FormControlLabel
                    about={opt.name}
                    control={
                        <Checkbox
                            checked={isChecked}
                            icon={<CheckBoxOutlineBlank />}
                            checkedIcon={<Square />}
                            className="checkbox"
                            disableFocusRipple={true}
                            disableRipple={true}
                            disableTouchRipple={true}
                            readOnly={true}
                            disabled={opt.readonly}
                            onChange={({ currentTarget }) =>
                                handleItemSelect(opt, currentTarget.checked)
                            }
                        />
                    }
                    label={<ListItemText primary={opt.name} />}
                />
            </MenuItem>
        );
    };

    return (
        <Box sx={{ height: menuHeight }} className={className}>
            <AutoSizer>
                {({ height, width }) => (
                    <List
                        height={height}
                        rowCount={options.length}
                        rowHeight={rowHeight}
                        rowRenderer={rowRenderer}
                        width={width}
                    />
                )}
            </AutoSizer>
        </Box>
    );
};

export default styled(MultiSelectList)(({ menuWidth }: IOwnProps) => ({
    width: menuWidth,
    ".MuiMenuItem-root": {
        padding: 0,
    },
    ".MuiSvgIcon-root": {
        height: 16,
    },
    ".MuiFormControlLabel-root": {
        width: "100%",
        margin: 0,
    },
}));
